<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
    <div class="container-fluid w-100 h-100 position-relative" style="overflow-x: hidden">
        <navbar :title="$t('observations.observations')" class="mb-1"/>
        <keep-alive>
            <observation-list
                :user="user"
                :is-map-viewer="isMapViewer"
                @search="searchObservations"
            />
        </keep-alive>
        <div
            v-if="results.length > 0"
            class="button-container col-sm-12 trip-buttons"
        >
            <button
                v-if="!reportVisible"
                :disabled="results.length < 1 || loading"
                class="btn btn-sm btn-outline-success form-button"
                @click="exportExcel"
            >
                {{ $t('trip.download_excel') }}
            </button>

            <button
                :disabled="results.length < 1 || loading"
                class="btn btn-sm btn-outline-success form-button"
                @click="exportRoadReport"
            >
                {{ $t('observations.download_road_report') }}
            </button>

            <button
                v-if="!reportVisible"
                :disabled="results.length < 1 || loading"
                class="btn btn-sm btn-outline-primary form-button"
                @click="showDetailsReport = true"
            >
                {{ $t('observations.details_report') }}
            </button>

            <button
                class="btn btn-sm btn-success form-button"
                :disabled="loading"
                @click="toggleReportView"
            >
                {{ toggleReportViewLabel }}
            </button>

            <button
                v-if="!reportVisible"
                :disabled="loading"
                class="btn btn-sm btn-primary form-button"
                @click="toggleMapView"
            >
                {{ toggleMapViewLabel }}
            </button>

        </div>

        <observation-result-summary
            v-if="!showDetailsReport && !reportVisible && results.length > 0"
            :results="results"
        />

        <observation-details-report
            v-if="showDetailsReport && observations.length > 0"
            :report-observations = "observations"
            :company-preferences="companyPreferences.value"
            :user="user"
            @close="showDetailsReport = false"
        />

        <!--Report View-->
        <div v-if="reportVisible" class="col-sm-12">
            <div class="header col-sm-12 button-container pt-1">
                <font-awesome-icon
                    v-if="roadReportData !== null"
                    icon="print"
                    class="print-icon"
                    @click.stop="print"
                />
            </div>
            <div id="printContainer" class="col-12 report" v-if="roadReportData !== null">
                <div class="header">
                    <span class="title">{{ $t('observations.road_report_title') }}</span>
                </div>
                <div class="col-sm-12 nopads coordinate-map dont-print">
                    <road-report-map
                        :observation-results="observations"
                        :show-selection-editor="showSelectionEditor"
                        :user="user"
                    />
                </div>

                <div class="col-sm-12 nopads">
                    <road-report-summary :reportData="roadReportData"/>
                    <hr class="report-divider"/>
                    <div v-for="(road, roadNumber) in roadReportData.roads" :key="roadNumber">
                        <div v-if="road.name === roadNumber" class="col-sm-12 report-heading" style="color: #0b4b8f">{{ road.name }}</div>
                        <div v-else class="col-sm-12 report-heading" style="color: #0b4b8f">{{ $t('road_reports.road_number') }} {{ roadNumber }} - {{ road.name }}</div>
                        <road-report-summary :reportData="road"/>
                        <div v-for="(section, sectionNumber) in road.sections" :key="sectionNumber">
                            <div class="col-sm-12 report-heading-sm mt-4">{{ $t('road_reports.road_section') }}
                                {{ sectionNumber }}
                            </div>
                            <road-report-summary :reportData="section"/>
                        </div>
                        <hr class="report-divider"/>
                    </div>
                </div>
            </div>
        </div>

        <!-- Map and results -->
        <div class="trip-results" v-show="!reportVisible && tableItems.length > 0">
            <div class="row">
                <!-- Observation list -->
                <div :class="{'col-md-6': mapVisible, 'col-12': !mapVisible}" class="mb-2">
                    <div id="tripResultContainer" class="col-sm-12 nopads">
                        <div class="col-sm-12 list-container nopads">
                            <div class="col-sm-12 list-content result-table">
                                <form class="d-flex align-items-center p-2 flex-wrap gap-2">
                                    <div class="form-check form-switch mx-2">
                                        <input
                                            class="form-check-input"
                                            type="checkbox"
                                            id="extended"
                                            v-model="extended"
                                        />
                                        <label class="form-check-label" for="extended">
                                            {{ $t('observations.extended_data') }}
                                        </label>
                                    </div>
                                    <button
                                        v-if="isObserver && selectedTableItems.length > 0"
                                        type="button"
                                        class="btn btn-primary btn-sm mx-2"
                                        @click="editSelected"
                                    >
                                        {{ $t('observations.edit_selected') }}
                                    </button>
                                </form>

                                <div v-if="tableItems.length > 0" class="col-12">
                                    <vue3-datatable
                                        ref="datatable"
                                        :rows="tableItems"
                                        :columns="fields"
                                        :pagination="false"
                                        :sticky-header="true"
                                        :height="tableItems.length >  10 ? '50.5em' : '100%'"
                                        :sortable="true"
                                        :has-checkbox="isObserver"
                                        :loading="loading"
                                        skin="bh-table-compact"
                                        :row-class="getRowClass"
                                        cell-class="routa-table-cell"
                                        @row-click="rowClicked"
                                        @row-select="rowSelected"
                                    >
                                        <template #created_time="data">
                                            {{ data.value.created }}
                                        </template>
                                        <template #closed_time="data">
                                            {{ data.value.closed }}
                                        </template>
                                        <template #assignment_status="data">
                                            <span v-if="data.value.assignment_id" class="nopads" @click.stop="fetchAssignment(data.value.assignment_id)">
                                                <font-awesome-icon
                                                    :title="$t('work_assignment.title')"
                                                    icon="clipboard-list"
                                                    :class="getAssignmentClass(data.value.assignment_status)"
                                                />
                                            </span>
                                        </template>
                                        <template #photo="data">
                                            <span class="nopads" @click.stop="showPhotoViewer(data.value.id)">
                                                <font-awesome-icon
                                                    v-if="data.value.photos && data.value.photos.length > 0"
                                                    icon="camera"
                                                    class="row-icon"/>
                                            </span>
                                        </template>
                                        <template #comments="data">
                                            <span class="icon-badge-wrapper" v-if="data.value?.comments > 0">
                                                <font-awesome-icon icon="comment" class="row-icon" />
                                                <span class="badge">{{ data.value.comments }}</span>
                                            </span>
                                        </template>
                                        <template #csv="data">
                                            <span class="nopads" @click.stop="downloadGeometry(data.value.id)">
                                            <font-awesome-icon
                                                :id="'coord_' + data.value.id"
                                                icon="file-csv"
                                                class="row-icon"
                                                :title="$t('observations.download_coordinates')"
                                            />
                                    </span>
                                        </template>
                                    </vue3-datatable>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="mapVisible" class="col-md-6 d-flex flex-column pb-0 mb-2">
                    <Transition name="fade">
                        <div class="flex-fill position-relative" style="height: 50vh; border-radius: .75em; border: 1px solid #BCBCBC; overflow: hidden">
                            <road-report-map
                                ref="roadReportMap"
                                :observation-results="observations"
                                :show-selection-editor="showSelectionEditor"
                                :select-marker-modal="selectMarkerModal"
                                :marker-selections="markerSelections"
                                :keep-view="keepMapPosition"
                                @updateObservations="closeAndUpdate"
                                @onPolylineTap="selectObservation"
                                @onMarkerTap="selectObservation"
                                @onMultiMarkerTap="onMultiMarkerTap"
                                @onCloseMarkerSelectionModal="closeMarkerSelectionModal"
                            />
                        </div>
                    </Transition>
                </div>
            </div>
        </div>

        <observation-editor
            v-if="editMode && selectedObservation"
            :observation="selectedObservation"
            :user="user"
            :is-observer="isObserver"
            :item-count="results.length"
            :index="selectedIndex"
            :is-selected="isCurrentObservationSelected"
            @close="closeForm"
            @closeAndUpdate="closeAndUpdate"
            @nextItem="editNext"
            @previousItem="editPrevious"
            @toggleObservationSelection="toggleObservationSelection"
        />

        <observation-bulk-editor
            v-if="editMode && selectedItems && selectedItems.length > 0"
            id="bulkEditor"
            :observations="selectedItems"
            :user="user"
            @close="closeForm"
            @closeAndUpdate="closeAndUpdate"
        />
        <Transition name="fade">
            <observation-photo-viewer
                v-if="photoObservation"
                :observation="photoObservation"
                @close="photoObservation = null"
            />
        </Transition>
        <base-modal v-if="assignment" hide-header hide-footer @close="assignment = null" size="lg">
            <work-assignment-editor
                :user="user"
                :is-observer="isObserver"
                :observation="assObservation"
                :work-assignment="assignment"
                @close="assignment = null"
                @closeAndUpdate="closeAndUpdate"/>
        </base-modal>
        <div
            v-if="loading"
            id="loader"
            class="spinner-loader"
        />
    </div>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import ObservationList from './ObservationList'
import {vehicleHelper} from '../mixins/VehicleMixin'
import {timeUtils} from '../mixins/TimeUtils'
import ObservationEditor from './ObservationEditor'
import {downloadHelper} from '../mixins/DownloadMixin'
import RoadReportSummary from './RoadReportSummary'
import RoadReportMap from '../map/RoadReportMap'
import {printHelper} from '../mixins/PrintMixin'
import {Printd} from "printd";
import {coordinateConverter} from "../mixins/CoordinateConversionMixin"
import ObservationBulkEditor from "@/components/observation/ObservationBulkEditor";
import ObservationDetailsReport from "@/components/observation/ObservationDetailsReport";
import ObservationPhotoViewer from "@/components/observation/ObservationPhotoViewer";
import {observationHelper} from "@/components/mixins/ObservationMixin";
import Navbar from "@/components/utils/Navbar";
import Vue3Datatable from '@bhplugin/vue3-datatable'
import {inject} from "vue";
import BaseModal from "../BaseModal.vue";
import WorkAssignmentEditor from "../workassignment/WorkAssignmentEditor.vue";
import {mapHelper} from "@/components/mixins/MapMixin.js";
import {workAssignmentHelper} from "../mixins/WorkAssignmentMixin.js";
import ObservationResultSummary from './ObservationResultSummary.vue'

export default {
    name: 'Observations',
    components: {
        WorkAssignmentEditor,
        BaseModal,
        Navbar,
        ObservationPhotoViewer,
        ObservationDetailsReport,
        ObservationBulkEditor,
        ObservationList,
        ObservationEditor,
        RoadReportSummary,
        RoadReportMap,
        Vue3Datatable,
        ObservationResultSummary
    },
    mixins: [timeUtils, restApi, vehicleHelper, downloadHelper, printHelper, coordinateConverter, observationHelper,
        workAssignmentHelper, mapHelper],
    props: {
        user: {
            type: Object,
            default: null
        },
        isMapViewer: Boolean,
        isObserver: Boolean
    },
    data: function () {
        return {
            loading: true,
            error: false,
            extended: false,
            results: [],
            roadReportData: null,
            tableItems: [],
            observationDetails: null,
            editMode: false,
            mapVisible: false,
            mapWasVisible: false,
            reportVisible: false,
            selectedTableItems: [],
            selectedItems: [],
            selectedObservation: null,
            selectMarkerModal: false,
            markerSelections: [],
            photoObservation: null,
            lastSearchedParams: null,
            strDetails: this.$t('observations.details'),
            selectAll: false,
            showDetailsReport: false,
            toggleMapViewLabel: this.$t('trip.show_map'),
            toggleMapViewSelectedLabel: this.$t('trip.show_map_selected'),
            toggleReportViewLabel: this.$t('observations.show_road_report'),
            showSelectionEditor: false,
            keepMapPosition: false,
            assignment: null,
            assObservation: null,
            selectedRowId: null,
            selectedIndex: null,
            simplifiedFields: [
                {
                    field: 'id',
                    title: this.$t('observations.id'),
                    sort: true,
                    headerClass: 'routa-table-header',
                    type: 'number'
                },
                {
                    field: 'created_time',
                    title: this.$t('observations.created_time'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'type_name',
                    title: this.$t('observations.type'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'length',
                    title: this.$t('observations.length'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'road_address',
                    title: this.$t('observations.road_location'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'contract',
                    title: this.$t('observations.contract'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'order',
                    title: this.$t('observations.order'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'reporter',
                    title: this.$t('observations.reporter'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'info',
                    title: this.$t('work_time.note'),
                    sort: true,
                    minWidth: '20em',
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'assignment_status',
                    title: '',
                    sort: false,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'photo',
                    title: '',
                    sort: false,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'comments',
                    title: '',
                    sort: false,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'csv',
                    title: '',
                    sort: false,
                    headerClass: 'routa-table-header'
                }
            ],
            fullFields: [
                {
                    field: 'id',
                    title: this.$t('observations.id'),
                    sort: true,
                    headerClass: 'routa-table-header',
                    type: 'number'
                },
                {
                    field: 'created_time',
                    title: this.$t('observations.created_time'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'type_name',
                    title: this.$t('observations.type'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'group_name',
                    title: this.$t('observations.group'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'length',
                    title: this.$t('observations.length'),
                    sort: true,
                    type: 'number',
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'road_direction',
                    title: this.$t('observations.direction'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'geometry_type',
                    title: this.$t('observations.geometry_type'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'road_number',
                    title: this.$t('observations.road_number'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'section_number',
                    title: this.$t('observations.road_section_number'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'distance_from_sec_start',
                    title: this.$t('observations.distance_from_sec_start'),
                    sort: true,
                    type: 'number',
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'road_section_length',
                    title: this.$t('observations.road_section_length'),
                    sort: true,
                    type: 'number',
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'contract',
                    title: this.$t('observations.contract'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'order',
                    title: this.$t('observations.order'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'reporter',
                    title: this.$t('observations.reporter'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'city',
                    title: this.$t('observations.city'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'street_name',
                    title: this.$t('observations.street_name'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'info',
                    title: this.$t('work_time.note'),
                    sort: true,
                    minWidth: '20em',
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'status',
                    title: this.$t('observations.status'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'assignment_status',
                    title: '',
                    sort: false,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'photo',
                    title: '',
                    sort: false,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'comments',
                    title: '',
                    sort: false,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'csv',
                    title: '',
                    sort: false,
                    headerClass: 'routa-table-header'
                }
            ],
            companyPreferences: {
                id: "",
                company_id: "",
                value: {
                    observationReportTitle: ""
                },
            },
            editedObservation: null,
            lastStatusGroup: null,
        }
    },
    watch: {
        "$i18n.locale"() {
            // Update button texts
            this.toggleMapViewLabel = this.selectedTableItems.length > 0 ? this.toggleMapViewSelectedLabel : this.$t('trip.show_map')
            this.toggleReportViewLabel = this.reportVisible ? this.$t('trip.show_table') : this.$t('observations.show_road_report')
        },
        mapVisible: function (value) {
            let showSelected = this.selectedTableItems.length > 0 ? this.toggleMapViewSelectedLabel : this.$t('trip.show_map')
            this.toggleMapViewLabel = value ? this.$t('trip.show_table') : showSelected
        },
        reportVisible: function (value) {
            this.toggleReportViewLabel = value ? this.$t('trip.show_table') : this.$t('observations.show_road_report')
        }
    },
    mounted: function () {
        this.loading = false
        this.d = new Printd()
        this.fetchCompanyPreferences()
    },
    computed: {
        observations() {
            if(this.selectedTableItems.length > 0) {
                return this.results.filter(result => {
                    return this.selectedTableItems.some(item => item.id === result.id);
                });
            } else {
                return this.results
            }
        },
        fields() {
            return this.extended ? this.fullFields : this.simplifiedFields
        },
        isCurrentObservationSelected() {
            if (!this.selectedObservation) return false
            return this.selectedTableItems.some(item => item.id === this.selectedObservation.id)
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    methods: {
        getAssignmentClass(status) {
            let classStr = 'row-icon'
            switch (status) {
                case this.OPEN: return classStr + ' icon-open'
                case this.IN_PROGRESS: return classStr + ' icon-in-progress'
                case this.ASSIGNED: return classStr + ' icon-assigned'
                case this.COMPLETED: return classStr + ' icon-completed'
                case this.CANCELLED: return classStr + ' icon-cancelled'
                case this.ARCHIVED: return classStr + ' icon-closed'
            }
            return classStr
        },


        fetchCompanyPreferences: function () {
            this.restFetch(this.companyPreferencesUrl, this.handleCompanyPreferenceResponse)
        },

        handleCompanyPreferenceResponse: function (response) {
            if (response.data) {
                this.companyPreferences.id = response.data.id || ""
                this.companyPreferences.company_id = response.data.company_id || ""

                // Preserve the value structure, merge with API data
                if (response.data.value && typeof response.data.value === 'object') {
                    this.companyPreferences.value = {
                        ...this.companyPreferences.value,
                        ...response.data.value
                    }
                }
            }
        },

        clearPolygon: function () {
            this.$nextTick(() => {
                this.$refs.roadReportMap.clearPolygon()
            })
        },
        rowSelected: function (selectedRows) {
            if (this.isObserver && !this._syncingSelection) {
                this.selectedTableItems = selectedRows
            }
        },

        toggleObservationSelection(observation) {
            if (!observation || !this.$refs.datatable) return
            const tableIdx = this.tableItems.findIndex(item => item.id === observation.id)
            if (tableIdx === -1) return
            const alreadySelected = this.selectedTableItems.some(item => item.id === observation.id)
            // Update selectedTableItems
            if (alreadySelected) {
                this.selectedTableItems = this.selectedTableItems.filter(item => item.id !== observation.id)
            } else {
                this.selectedTableItems = [...this.selectedTableItems, this.tableItems[tableIdx]]
            }
            // Re-sync datatable checkboxes without triggering rowSelected
            this._syncingSelection = true
            this.$refs.datatable.clearSelectedRows()
            this.$nextTick(() => {
                this.selectedTableItems.forEach(selected => {
                    const idx = this.tableItems.findIndex(item => item.id === selected.id)
                    if (idx !== -1) this.$refs.datatable.selectRow(idx)
                })
                this._syncingSelection = false
            })
        },

        rowClicked(item) {
            this.selectedRowId = item ? item.id : null
            this.selectedIndex = item ? this.results.findIndex(o => o.id === item.id) : null
            this.editItem(item)
        },

        getRowClass(row) {
            return row.id === this.selectedRowId ? 'routa-table-row selected-row row-id-' + row.id : 'routa-table-row  row-id-' + row.id
        },

        setSelection(index) {
            this.selectedRowId = null
            this.selectedIndex = index
            if (index >= 0 && index < this.results.length) {
                this.selectedRowId = this.results[index].id
            }
        },

        resetSelection() {
            this.selectedRowId = null
            this.$refs.roadReportMap?.resetCenter()
        },

        async scrollToSelected() {
            let rowElement = null
            if (this.selectedRowId) {
                rowElement = document.querySelector(`.row-id-${this.selectedRowId}`)
                if(!rowElement) {
                    // Check if we have something in the index - observation closing case -> need to return to same place in list
                    if (this.selectedIndex && this.selectedIndex >= 0) {
                        this.selectedIndex = Math.min(this.selectedIndex, this.results.length)
                        const scrollId = this.results[this.selectedIndex].id
                        rowElement = document.querySelector(`.row-id-${scrollId}`)
                    }
                }
            }
            if (rowElement) {
                rowElement.scrollIntoView({ behavior: 'auto', block: 'center' })
            } else {
                this.resetSelection()
            }
        },

        openEditor: function () {
            this.editMode = true
        },
        editItem: function (observation) {
            const o = this.results.find(item => item.id === observation.id)
            this.editObservation(observation.id)
        },
        selectObservation(id) {
            var observation = this.results.find(o => o.id === id)
            if (observation) {
                this.selectedObservation = observation
                this.selectedRowId = id
                this.scrollToSelected()
            }
        },
        editObservation: function (id) {
            this.closeMarkerSelectionModal()
            this.selectedItems = []
            var observation = this.results.find(o => o.id === id)
            if (observation) {
                this.selectedObservation = observation
                this.selectedRowId = id
                this.scrollToSelected()
                this.openEditor()
            }
        },
        editSelected: function () {
            this.selectedObservation = null
            this.selectedItems = []
            const items = []
            items.push(...this.selectedTableItems)
            items.forEach(obj => {
                const observation = this.results.find(o => o.id === obj.id);
                if (observation) {
                    this.selectedItems.push(observation)
                }
            })
            // Open large editor if only one is selected
            if (this.selectedItems.length === 1){
                this.selectedObservation = this.selectedItems[0]
                this.selectedItems = []
            }
            this.openEditor()
        },
        searchObservations(params) {
            this.keepMapPosition = false
            this.lastStatusGroup = params.status ?? null
            this.resetSelection()
            this.selectedTableItems = []
            this.fetchObservations(params)
        },
        onMultiMarkerTap(data) {
            const sources = {
                [this.OBSERVATION]: () => this.observations,
            };
            const elements = data
                .map(data => {
                    const src = sources[data.type];
                    if (src == null) {
                        return null;
                    }
                    let element = {
                        type: data.type,
                        id: data.id,
                    }

                    element.data = src().find(el => el.id === data.id);
                    return element
                })
                .filter(el => el != null);
            this.selectMarkerModal = true
            this.markerSelections = elements;
        },
        closeMarkerSelectionModal() {
            if(this.selectMarkerModal) {
                this.selectMarkerModal = false
                this.markerSelections = []
            }
        },
        fetchObservations: function (params) {
            this.resetPage()
            this.loading = true
            this.lastSearchedParams = params
            this.restFetchParams(this.observationUrl, this.lastSearchedParams, this.handleResponse, this.handleError)
        },
        resetPage: function () {
            this.editedObservation = false
            this.error = false
            this.reportVisible = false
        },
        handleError: function (error) {
            this.loading = false
            if (error.response && error.response.status && error.response.status === 500) {
                this.showAlert(this.$t('observations.err_too_much'))
            } else {
                this.showAlert(this.$t('observations.err_general'))
            }
        },

        handleResponse: function (response) {
            this.loading = false
            this.results = response.data
            this.tableItems = []
            let temp = []
            this.results.forEach(function (observation) {
                if (observation.can_edit === true) {
                    temp.push(this.getTableItem(observation))
                }
            }, this)
            this.$nextTick(() => {
                this.tableItems = temp
                this.$nextTick(() => {
                    this.scrollToSelected()
                    // Restore selection
                    if (this.selectedTableItems.length > 0) {
                        const indexes = this.selectedTableItems.map(row => this.tableItems.findIndex(item => item.id === row.id));
                        indexes.forEach(idx => this.$refs.datatable.selectRow(idx))
                    }
                })
            })
        },
        toggleMapView: function () {
            this.mapVisible = !this.mapVisible
            this.showSelectionEditor = true
        },
        toggleReportView: function () {
            if (!this.reportVisible) {
                this.loading = true
                this.roadReportData = null
                this.showSelectionEditor = false
                this.restFetchParams(this.observationRoadReportDataUrl, this.lastSearchedParams, this.handleReportDataResponse, this.handleError)
            } else {
                this.reportVisible = false
            }
        },
        handleReportDataResponse: function (response) {
            this.roadReportData = response.data
            this.reportVisible = true
            this.loading = false
        },
        closeForm: function () {
            this.editMode = false
        },
        closeAndUpdate: function () {
            this.keepMapPosition = true
            this.editMode = false
            this.assignment = null
            this.assObservation = null
            this.fetchObservations(this.lastSearchedParams)
        },
        editNext() {
            this.selectedObservation = null
            this.editMode = false
            this.$nextTick(() => {
                if (this.selectedIndex < this.results.length - 1){
                    this.selectedIndex++
                    const item = this.results[this.selectedIndex]
                    this.selectedRowId = item.id
                    this.editItem(item)
                }
            })
        },
        editPrevious() {
            this.selectedObservation = null
            this.editMode = false
            this.$nextTick(() => {
                if (this.selectedIndex > 0) {
                    this.selectedIndex--
                    const item = this.results[this.selectedIndex]
                    this.selectedRowId = item.id
                    this.editItem(item)
                }
            })
        },
        getTableItem: function (observation) {
            if (observation.closed_time !== undefined) {
                observation.closed_time = this.setTime(observation.closed_time)
            }
            let contract = observation.contract ? observation.contract.name : undefined
            if (!contract && observation.work_order) {
                contract = observation.work_order.contract_task_type.contract ? observation.work_order.contract_task_type.contract.name : '-'
            }

            return {
                id: observation.id,
                reporter: observation.reporter ? observation.reporter.first_name + ' ' + observation.reporter.last_name : "-",
                type_name: observation.type.name,
                group_name: observation.type.observation_group.name,
                geometry_type: observation.type.geometry_type === 1 ? this.$t('observations.point') : this.$t('observations.line'),
                length: observation.length ? observation.length : '',
                created_time: new Date(observation.created_time).getTime(),
                created: this.toLocalTime(observation.created_time),
                closed_time: observation.closed_time ? new Date(observation.closed_time).getTime() : null,
                closed: observation.closed_time ? this.toLocalTime(observation.closed_time) : null,
                city: observation.city ? observation.city.name : '',
                street_name: observation.street_name ? observation.street_name : '',
                road_number: observation.road_number && observation.road_number > 0 ? observation.road_number : '',
                section_number: observation.section_number &&  observation.section_number > 0 ? observation.section_number : '',
                distance_from_sec_start: observation.distance_from_sec_start && observation.distance_from_sec_start >= 0? observation.distance_from_sec_start : '',
                road_section_length: observation.road_section_length ? observation.road_section_length : '',
                contract: contract,
                order: observation.work_order ? observation.work_order.task_type.name : '',
                status: (observation.closed_time ? this.$t('observations.closed') : this.$t('observations.open')),
                observation_group: observation.type.observation_group.name,
                road_direction: observation.road_direction,
                road_lane: observation.road_lane,
                info: observation.info ? observation.info : '',
                photos: observation.photos,
                road_address: this.getStreetAddressString(observation),
                assignment_id: observation.assignment_id,
                assignment_status: observation.assignment_status,
                comments: observation.comments
            }
        },
        exportExcel: function () {
            this.loading = true
            let params = this.getCommonReportParams(JSON.parse(JSON.stringify(this.lastSearchedParams)))
            if (this.selectedTableItems.length > 0) {
                params.observations = this.selectedTableItems.map(item => item.id)
            }
            this.restGetDownloadFile(this.observationReportUrl, params, this.excelDownloadSuccess, this.fileDownloadFail)
        },
        excelDownloadSuccess: function (response) {
            this.loading = false
            this.downloadFile(response, this.$t('observations.report_file_name'))
        },
        fileDownloadFail: function () {
            this.loading = false
            this.showAlert(this.$t('common.report_generation_failed'))
        },
        exportRoadReport: function () {
            this.loading = true
            this.restGetDownloadFile(this.observationRoadReportUrl, this.getCommonReportParams(this.lastSearchedParams), this.roadReportDownloadSuccess, this.fileDownloadFail)
        },
        roadReportDownloadSuccess: function (response) {
            this.loading = false
            this.downloadFile(response, this.$t('observations.road_report_file_name'))
        },
        print: function () {
            if (!this.loading) {
                this.d.print(document.getElementById('printContainer'), this.printStyle)
            }
        },
        downloadGeometry: function (id) {
            let observation = this.results.find(item => item.id === id)
            if (observation) {
                let csvData = this.getGeometryAsCsv(observation)
                if (csvData) {
                    // Generate csv and download
                    let blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
                    let url = URL.createObjectURL(blob);
                    let pom = document.createElement('a');
                    pom.href = url;
                    pom.setAttribute('download', 'Routa_geometry.csv');
                    pom.click();
                }
            }
        },
        getGeometryAsCsv: function (observation) {
            let result = ''
            let data = []
            if (observation.geometry.point) {
                data = [[observation.geometry.point.x, observation.geometry.point.y]]
            } else {
                data = observation.geometry.line_string.points
            }
            data.forEach(point => {
                let tm35FinCoords = this.wgs84ToTm35Fin(point[1], point[0])
                result += tm35FinCoords[0] + ',' + tm35FinCoords[1] + '\r\n'
            })
            return result
        },
        showPhotoViewer(observation) {
            this.photoObservation = this.results.find(item => item.id === observation)
        },
        fetchAssignment: function (id) {
            if (!this.loading) {
                this.loading = true
                this.restFetchParams(this.workAssignmentUrl, {id: id}, this.handleAssignment)
            }
        },
        handleAssignment(response) {
            this.loading = false
            if (response && response.data && response.data.length === 1) {
                this.assignment = response.data[0]
                this.assObservation = this.assignment?.observation
            } else {
                this.assignment = null
                this.assObservation = null
            }
        }
    }
}
</script>

<style scoped lang="css">
.spinner-loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;
    border: .3em solid #404041;
    border-radius: 50%;
    border-top: .3em solid #7FC000;
    width: 3em;
    height: 3em;
    -webkit-animation: spin 2s linear infinite;
    animation: spin 1s linear infinite;
}


.icon-badge-wrapper {
    position: relative;
    display: inline-block;
}

.badge {
    position: absolute;
    top: -4px;
    right: -6px;
    background: #A4D300;
    color: #0b4b8f;
    border-radius: 50%;
    padding: 1px 5px;
    font-size: 10px;
    font-weight: bold;
    line-height: 1;
}

</style>
